import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuBuilding2, LuCircleCheck, LuCircleOff } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import AddCenterModal from './AddCenterModal';
import SMOOverviewModal from './SMOOverviewModal';
import RegionManagementModal from './RegionManagementModal';
import useCenters from './useCenters';
import useCenterText from './centerText';
import {
  CENTER_COLUMNS,
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  getFilterState,
} from './centerColumns';
import CenterHeader from './parts/CenterHeader';
import CenterCell, { CenterCardRow, makeCellTitle } from './parts/CenterCell';
import CenterDetailPane from './parts/CenterDetailPane';
import DeleteCenterDialog from './parts/DeleteCenterDialog';

const COLUMNS_STORAGE_KEY = 'centerManagement.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;
const ALL_STATUSES = 'ALL';

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Merkez Yönetimi (Tailwind), built like Çalışan / Müşteri Yönetimi: center
 * tiles, a table card that reaches at most the bottom of the screen with
 * header filters, an Aktif / İnaktif / Tümü switch, a column menu and "go to
 * page", and the selected center's panel beside the table. The data logic
 * lives in useCenters; the region map and the SMO overview open as dialogs.
 */
const CenterManagementPage = () => {
  const {
    isLoading, centers, filteredCenters, employeesMap,
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    activeCount, inactiveCount,
    getSMOName, getSMOBackupName, getRegionForCenter, getRegionManagerName,
    selectedCenter, setSelectedCenter, handleCenterSelect,
    isAddModalOpen, setIsAddModalOpen, isEditModalOpen, setIsEditModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen, isSmoOverviewOpen, setIsSmoOverviewOpen,
    isRegionModalOpen, setIsRegionModalOpen,
    centerToDelete, openDeleteModal, handleDeleteCenter,
    handleStatusToggle, handleEditCenter, handleCenterUpdate,
    fetchCenters, fetchRegions, downloadExcel,
  } = useCenters();
  const { language, locale, label } = useCenterText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: CENTER_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [checkingDelete, setCheckingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The add form starts empty again after a center was added
  const [addFormKey, setAddFormKey] = useState(0);
  const addFormUsedRef = useRef(false);
  const hasLoaded = centers.length > 0 || !isLoading;

  const lookup = { getSMOName, getSMOBackupName, getRegionForCenter, getRegionManagerName };

  // Süzgeç ya da arama değişince ilk sayfaya dönülür
  const resetKey = JSON.stringify([filters, searchTerm]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredCenters, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // The selection follows refreshed data and is dropped once the center is
  // gone (never while the edit form is open: it reads the selected center)
  useEffect(() => {
    if (!selectedCenter || isEditModalOpen) return;
    const current = centers.find((center) => center.id === selectedCenter.id);
    if (!current) setSelectedCenter(null);
    else if (current !== selectedCenter) setSelectedCenter(current);
  }, [centers, selectedCenter, isEditModalOpen, setSelectedCenter]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedCenter) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedCenter(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCenter, showFilterSheet, setSelectedCenter]);

  const columnLabel = useCallback((key) => label(`columns.${CENTER_COLUMNS[key].id}`), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: CENTER_COLUMNS[key].type ? { type: CENTER_COLUMNS[key].type } : undefined,
  })), [columns.visibleKeys, columnLabel]);

  // Durum is shown by the switch, so it gets no chip
  const filterChips = Object.keys(filters)
    .filter((key) => key !== 'Durum' && CENTER_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(filters[key]), state: getFilterState(key, filters[key]) }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm);
  const statusValue = filters['Durum'] || ALL_STATUSES;
  const setStatus = (value) => handleFilter('Durum', value === ALL_STATUSES ? '' : value);

  const openNewCenter = () => {
    if (addFormUsedRef.current) {
      addFormUsedRef.current = false;
      setAddFormKey((key) => key + 1);
    }
    setIsAddModalOpen(true);
  };

  // "Tabloyu İndir": tüm merkezler, ekranda görünen sütunlarla (eski sırayla)
  const exportExcel = () => downloadExcel(Object.fromEntries(COLUMN_KEYS.map((key) => [key, Boolean(columns.visibleColumns[key])])));

  const requestDelete = async () => {
    if (!selectedCenter || checkingDelete) return;
    setCheckingDelete(true);
    try {
      await openDeleteModal(selectedCenter);
    } finally {
      setCheckingDelete(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await handleDeleteCenter();
    } finally {
      setDeleting(false);
    }
  };

  // "Sayfaya git": 1 ile son sayfa arasında olmalı
  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedCenter ? filteredCenters.findIndex((center) => center.id === selectedCenter.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredCenters[selectedIndex + offset];
    if (!sibling) return;
    setSelectedCenter(sibling);
    reveal(sibling.id);
  };

  const total = centers.length;
  const shareOf = (count) => (total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: total, icon: LuBuilding2, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', onClick: () => setStatus(ALL_STATUSES), active: statusValue === ALL_STATUSES },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: activeCount, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(activeCount), shareTitle: shareTitle(activeCount), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => setStatus('Aktif'), active: statusValue === 'Aktif' },
    { key: 'inactive', label: label('stats.inactive'), shortLabel: label('stats.inactiveShort'), value: inactiveCount, icon: LuCircleOff, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', share: shareOf(inactiveCount), shareTitle: shareTitle(inactiveCount), barClass: 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500', onClick: () => setStatus('İnaktif'), active: statusValue === 'İnaktif' },
  ];

  const paneProps = {
    center: selectedCenter,
    lookup,
    checkingDelete,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredCenters.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredCenters.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedCenter(null),
    onEdit: () => handleEditCenter(selectedCenter),
    onDelete: requestDelete,
    onToggleStatus: () => handleStatusToggle(selectedCenter),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <CenterHeader
          onRegions={() => setIsRegionModalOpen(true)}
          onSmoOverview={() => setIsSmoOverviewOpen(true)}
          onExcel={exportExcel}
          onAdd={openNewCenter}
        />

        <StatTiles tiles={tiles} ready={hasLoaded} />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: [
                  { value: 'Aktif', label: label('status.active') },
                  { value: 'İnaktif', label: label('status.inactive') },
                  { value: ALL_STATUSES, label: label('status.all') },
                ],
                value: statusValue,
                onChange: setStatus,
                layoutId: 'center-status-filter',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(center) => <CenterCardRow center={center} lookup={lookup} />}
                selectedId={selectedCenter?.id}
                onSelect={handleCenterSelect}
                isLoading={isLoading}
                isBusy={isLoading && centers.length > 0}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(center, key) => <CenterCell columnKey={key} center={center} lookup={lookup} />}
                getCellTitle={makeCellTitle(lookup, label)}
                selectedId={selectedCenter?.id}
                onSelect={handleCenterSelect}
                isLoading={isLoading}
                isBusy={isLoading && centers.length > 0}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredCenters.length}
              rangeOf={label('table.rangeOf', { total: filteredCenters.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading && centers.length > 0}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <CenterDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <CenterDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.filter((key) => CENTER_COLUMNS[key].type).map((key) => ({
          key,
          label: columnLabel(key),
          type: CENTER_COLUMNS[key].type,
        }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredCenters.length}
      />

      <AddCenterModal
        key={addFormKey}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={() => {
          addFormUsedRef.current = true;
          setIsAddModalOpen(false);
          fetchCenters();
        }}
        employeesMap={employeesMap}
      />

      <AddCenterModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleCenterUpdate}
        centerData={selectedCenter}
        isEditing={true}
        employeesMap={employeesMap}
      />

      <RegionManagementModal
        isOpen={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        employeesMap={employeesMap}
        onSaved={fetchRegions}
        centers={centers}
      />

      <SMOOverviewModal
        isOpen={isSmoOverviewOpen}
        onClose={() => setIsSmoOverviewOpen(false)}
        centers={centers}
        employeesMap={employeesMap}
        onCenterUpdated={fetchCenters}
      />

      <DeleteCenterDialog
        center={isDeleteModalOpen ? centerToDelete : null}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </>
  );
};

export default CenterManagementPage;
